import { LoadingButton } from "@mui/lab";
import { CircularProgress, Paper, Snackbar, snackbarContentClasses } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Redirect, Route, Switch, useHistory, useLocation, useParams } from "react-router-dom";
import { useRegisterSW } from "virtual:pwa-register/react";

import { Loading } from "components";
import { StorageKey } from "config/storage";
import { ConfigInitStatus, useInitConfig } from "hooks/useInitConfig";
import { PreRefreshState } from "hooks/useRefreshAndRestoreState";
import { useSetAuthState } from "hooks/useSetAuthState";
import { Explorer } from "pages/explorer";
import { explorerActions, selectConfig } from "slices/explorer";
import { getEnableProcoreBetaFromURL } from "slices/explorer/utils";
import { telemetry } from "telemetry";
import { getOAuthState, getUser } from "utils/auth";
import { isAppBuild } from "utils/buildType";
import { GeneralProvider } from "utils/generalProvider";
import { deleteFromStorage, getFromStorage, saveToStorage } from "utils/storage";

import { useAppDispatch, useAppSelector } from "./redux-store-interactions";

enum Status {
    Initial,
    Loading,
    Ready,
}

export function App() {
    const history = useHistory();

    const [authStatus, setAuthStatus] = useState(Status.Initial);
    const config = useAppSelector(selectConfig);
    const dispatch = useAppDispatch();
    const useTokenFromUrl = window.top !== self && new URLSearchParams(window.location.search).get("accessToken");

    const {
        needRefresh: [needRefresh],
        updateServiceWorker,
    } = useRegisterSW({
        immediate: false,
    });

    const configStatus = useInitConfig();

    useEffect(() => {
        const state = getOAuthState();

        if (state && state.sceneId) {
            history.replace(`/${state.sceneId}${window.location.search}`);
        }
    }, [history]);

    useEffect(() => {
        const handleOnline = () => dispatch(explorerActions.toggleIsOnline(true));
        const handleOffline = () => dispatch(explorerActions.toggleIsOnline(false));
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, [dispatch]);

    const setAuthState = useSetAuthState();

    const authenticating = useRef(false);
    useEffect(() => {
        handleOAuth();

        async function handleOAuth() {
            if (
                configStatus !== ConfigInitStatus.Ready ||
                authStatus !== Status.Initial ||
                authenticating.current ||
                useTokenFromUrl
            ) {
                return;
            }

            const state = getOAuthState();

            setAuthStatus(Status.Loading);
            authenticating.current = true;

            const params = new URLSearchParams(window.location.search);
            const code = params.get("code");
            const tokenUrl = "/token";

            if (state?.service === "self" && code) {
                window.history.replaceState(null, "", `${window.location.pathname}${state.query ?? ""}`);
                dispatch(explorerActions.setLocalBookmarkId(state.localBookmarkId));

                const res:
                    | {
                          access_token: string;
                          expires_in: number;
                          refresh_token: string;
                          refresh_token_expires_in: number;
                          token_type: string;
                      }
                    | undefined = await fetch(tokenUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: new URLSearchParams({
                        grant_type: "authorization_code",
                        client_id: config.novorenderClientId,
                        code: code,
                        redirect_uri: window.location.origin,
                        code_verifier: getFromStorage(StorageKey.CodeVerifier),
                    }),
                })
                    .then((res) => {
                        if (!res.ok) {
                            throw res.statusText;
                        }
                        return res.json();
                    })
                    .then((res) => {
                        if (res.access_token) {
                            return res;
                        } else {
                            throw res;
                        }
                    })
                    .catch((e) => {
                        console.warn(e);
                        telemetry.trackCaughtException({
                            source: "app.handleOAuth.tokenExchange",
                            category: "auth",
                            error: e,
                        });
                    });

                if (res) {
                    const user = await getUser(res.access_token);

                    if (user) {
                        saveToStorage(
                            StorageKey.RefreshToken,
                            JSON.stringify({
                                token: res.refresh_token,
                                expires: Date.now() + res.refresh_token_expires_in * 1000,
                            }),
                        );
                        setAuthState(res.access_token, user);
                    }
                }
            } else {
                const storedRefreshToken = getFromStorage(StorageKey.RefreshToken);

                if (!storedRefreshToken) {
                    setAuthStatus(Status.Ready);
                    return;
                }

                try {
                    const parsedToken = JSON.parse(storedRefreshToken) as { token: string; expires: number };

                    const res = await fetch(tokenUrl, {
                        method: "POST",
                        headers: { "Content-Type": "application/x-www-form-urlencoded" },
                        body: new URLSearchParams({
                            grant_type: "refresh_token",
                            client_id: config.novorenderClientId,
                            refresh_token: parsedToken.token,
                        }),
                    })
                        .then((res) => {
                            if (!res.ok) {
                                throw res.statusText;
                            }
                            return res.json();
                        })
                        .then((res) => {
                            if (res.access_token) {
                                return res;
                            } else {
                                throw res;
                            }
                        });

                    if (res) {
                        const user = await getUser(res.access_token);

                        if (user) {
                            saveToStorage(
                                StorageKey.RefreshToken,
                                JSON.stringify({
                                    token: res.refresh_token,
                                    expires: parsedToken.expires,
                                }),
                            );

                            setAuthState(res.access_token, user);
                        }
                    }
                } catch (e) {
                    telemetry.trackCaughtException({
                        source: "app.handleOAuth.tokenRefresh",
                        category: "auth",
                        error: e,
                    });
                    deleteFromStorage(StorageKey.RefreshToken);
                }
            }

            setAuthStatus(Status.Ready);
        }
    }, [history, dispatch, authStatus, config, configStatus, useTokenFromUrl, setAuthState]);

    useEffect(() => {
        handleIframeAuth();

        async function handleIframeAuth() {
            if (
                configStatus !== ConfigInitStatus.Ready ||
                authStatus !== Status.Initial ||
                authenticating.current ||
                !useTokenFromUrl
            ) {
                return;
            }

            const accessToken = new URLSearchParams(window.location.search).get("accessToken");
            const user = accessToken ? await getUser(accessToken) : undefined;

            if (accessToken && user) {
                setAuthState(accessToken, user);
            }
            setAuthStatus(Status.Ready);
        }
    }, [authStatus, configStatus, dispatch, useTokenFromUrl, setAuthState]);

    useEffect(() => {
        const localBookmark = getFromStorage(StorageKey.PreRefreshState);
        if (localBookmark) {
            deleteFromStorage(StorageKey.PreRefreshState);
            try {
                const parsed = JSON.parse(localBookmark) as PreRefreshState;
                dispatch(explorerActions.setWidgets(parsed.widgets));
                dispatch(explorerActions.setMaximized(parsed.maximized));
                dispatch(explorerActions.setMaximizedHorizontal(parsed.maximizedHorizontal));
                dispatch(explorerActions.setLocalBookmarkId(parsed.localBookmarkId));
            } catch (e) {
                console.warn("Error restoring pre-refresh state", e);
            }
        }
    }, [dispatch]);

    useEffect(() => {
        // For standalone app enable Procore Beta
        // because it's only used for testing/demo.
        // Can override with query param
        dispatch(explorerActions.setIsProcoreBeta(getEnableProcoreBetaFromURL() ?? true));
    }, [dispatch]);

    return (
        <>
            <GeneralProvider>
                {authStatus !== Status.Ready || configStatus !== ConfigInitStatus.Ready ? (
                    <Loading />
                ) : (
                    <>
                        {isAppBuild() && needRefresh && (
                            <UpdatePrompt
                                update={() => {
                                    updateServiceWorker(true);
                                }}
                            />
                        )}
                        <Switch>
                            <Route path="/callback">
                                <Loading />
                            </Route>
                            <Route path="/explorer/:id?">
                                <Explorer />
                            </Route>
                            <Route path="/login/:id?">
                                <RedirectLegacyLoginUrl />
                            </Route>
                            <Route path="/:id?">
                                <Explorer />
                            </Route>
                        </Switch>
                    </>
                )}
            </GeneralProvider>
        </>
    );
}

function UpdatePrompt({ update }: { update: () => void }) {
    const { t } = useTranslation();
    const [updating, setUpdating] = useState(false);

    return (
        <Snackbar
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{
                bottom: { xs: 132, sm: "auto" },
                top: { xs: "auto", sm: 32 },
                [`& .${snackbarContentClasses.root}`]: {
                    py: 2,
                },
            }}
            open={true}
            message={t("newVersionAvailable")}
        >
            <Paper
                sx={{
                    px: 2,
                    py: 1.5,
                    background: (theme) => theme.palette.secondary.main,
                    color: "#fff",
                }}
            >
                <>
                    {t("newVersionAvailable")}
                    <LoadingButton
                        loading={updating}
                        loadingIndicator={<CircularProgress size={20} />}
                        variant={"contained"}
                        size="small"
                        onClick={() => {
                            setUpdating(true);
                            update();
                        }}
                        sx={{ ml: 2, color: "#fff" }}
                    >
                        {t("update")}
                    </LoadingButton>
                </>
            </Paper>
        </Snackbar>
    );
}

function RedirectLegacyLoginUrl() {
    const location = useLocation();
    const params = useParams();

    return <Redirect to={generatePath(location.pathname.replace("/login", ""), params) + location.search} />;
}
